import { useState } from 'react';
import { 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  ChevronDown, 
  ChevronUp,
  Shield,
  TrendingUp,
  TrendingDown,
  Scale,
  Layers,
  Target,
  Calendar,
  AlertOctagon,
} from 'lucide-react';

const SEVERITY_CONFIG = {
  error: { 
    icon: AlertOctagon, 
    label: 'Violations', 
    bg: 'bg-red-500/10', 
    border: 'border-red-500/30', 
    text: 'text-red-400',
    badge: 'bg-red-500/20 text-red-400',
  },
  warning: { 
    icon: AlertTriangle, 
    label: 'Warnings', 
    bg: 'bg-amber-500/10', 
    border: 'border-amber-500/30', 
    text: 'text-amber-400',
    badge: 'bg-amber-500/20 text-amber-400',
  },
  info: { 
    icon: Info, 
    label: 'Notices', 
    bg: 'bg-sky-500/10', 
    border: 'border-sky-500/30', 
    text: 'text-sky-400',
    badge: 'bg-sky-500/20 text-sky-400',
  },
};

const RULE_ICONS = {
  concentration: Scale,
  profit_taking: TrendingUp,
  loss_review: TrendingDown,
  category_limit: Layers,
  cluster_concentration: Target,
  valuation_envelope: TrendingUp,
  valuation_breach: TrendingUp,
  earnings_proximity: Calendar,
  break_condition: AlertTriangle,
};

function formatValue(value, rule) {
  if (value === null || value === undefined) return 'N/A';
  // Valuation rules use raw ratios (PE=78.0), not decimal fractions
  const isRatio = rule === 'valuation_breach' || rule === 'valuation_envelope';
  if (isRatio) return value.toFixed(1);
  return `${(value * 100).toFixed(1)}%`;
}

export default function AttentionRequired({ violations = [], warnings = [] }) {
  const [expanded, setExpanded] = useState(true);
  
  // Group violations by severity
  const grouped = { error: [], warning: [], info: [] };
  violations.forEach(v => {
    const sev = v.severity || 'warning';
    if (grouped[sev]) grouped[sev].push(v);
    else grouped.warning.push(v);
  });
  
  // Warnings from check_rules are string messages (layers 2, 5, 6)
  const warningMessages = warnings.map((w, i) => ({
    id: `warn-${i}`,
    message: typeof w === 'string' ? w : w.message || String(w),
    severity: 'warning',
    rule: 'rule_warning',
  }));
  
  const totalCount = violations.length + warningMessages.length;
  
  if (totalCount === 0) return null;
  
  const errorCount = grouped.error.length;
  const warningCount = grouped.warning.length + warningMessages.length;
  const infoCount = grouped.info.length;
  
  return (
    <div className="card border border-amber-500/20">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <h3 className="card-header mb-0">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          Attention Required
          <span className="ml-2 text-sm font-normal text-slate-400">
            {errorCount > 0 && <span className="text-red-400 mr-2">{errorCount} violation{errorCount !== 1 ? 's' : ''}</span>}
            {warningCount > 0 && <span className="text-amber-400 mr-2">{warningCount} warning{warningCount !== 1 ? 's' : ''}</span>}
            {infoCount > 0 && <span className="text-sky-400">{infoCount} notice{infoCount !== 1 ? 's' : ''}</span>}
          </span>
        </h3>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        )}
      </button>
      
      {expanded && (
        <div className="mt-4 space-y-3">
          {/* Error violations */}
          {grouped.error.map((v, i) => (
            <ViolationRow key={`err-${i}`} violation={v} />
          ))}
          
          {/* Warning violations */}
          {grouped.warning.map((v, i) => (
            <ViolationRow key={`warn-v-${i}`} violation={v} />
          ))}
          
          {/* String warnings from rules engine */}
          {warningMessages.map((w) => (
            <div key={w.id} className="flex items-start gap-3 p-2 rounded-lg bg-amber-500/5 border border-amber-500/10">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <p className="text-sm text-slate-300">{w.message}</p>
            </div>
          ))}
          
          {/* Info violations */}
          {grouped.info.map((v, i) => (
            <ViolationRow key={`info-${i}`} violation={v} />
          ))}
        </div>
      )}
    </div>
  );
}

function ViolationRow({ violation }) {
  const sev = SEVERITY_CONFIG[violation.severity] || SEVERITY_CONFIG.warning;
  const RuleIcon = RULE_ICONS[violation.rule] || Shield;
  const SevIcon = sev.icon;
  
  return (
    <div className={`flex items-start gap-3 p-2 rounded-lg ${sev.bg} border ${sev.border}`}>
      <SevIcon className={`w-4 h-4 ${sev.text} mt-0.5 shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <RuleIcon className="w-3.5 h-3.5 text-slate-400" />
          {violation.ticker && (
            <span className="font-mono font-medium text-sm text-slate-200">{violation.ticker}</span>
          )}
          {violation.category && !violation.ticker && (
            <span className="text-sm text-slate-300">{violation.category}</span>
          )}
          <span className={`text-xs px-1.5 py-0.5 rounded ${sev.badge}`}>
            {violation.rule?.replace(/_/g, ' ')}
          </span>
        </div>
        <p className="text-sm text-slate-400 mt-0.5">{violation.message}</p>
        {violation.current_value !== undefined && violation.threshold !== undefined && (
          <p className="text-xs text-slate-500 mt-0.5">
            Current: {formatValue(violation.current_value, violation.rule)} | Threshold: {formatValue(violation.threshold, violation.rule)}
          </p>
        )}
      </div>
    </div>
  );
}
